import { useEffect, useState } from 'react';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { useApi, formatPrice } from '../../lib/api';

type SellerListing = {
  listing_id: string;
  status: string;
  price_per_seat_cents: number;
  quantity_available: number;
  quantity_sold: number;
  section: string;
  row: string;
  seats_json: string;
  opponent: string;
  game_date: string;
};

type SeatBlock = {
  seat_block_id: string;
  section: string;
  row: string;
  seats_json: string;
  seat_count: number;
  section_type: string;
  verified: number;
};

type Game = { game_id: string; opponent: string; game_date: string };

export default function SellerListings() {
  const api = useApi();
  const [listings, setListings] = useState<SellerListing[]>([]);
  const [seatBlocks, setSeatBlocks] = useState<SeatBlock[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [form, setForm] = useState({ seat_block_id: '', game_id: '', price: '', qty: 1, note: '' });
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    const [a, b, g] = await Promise.all([
      api<{ listings: SellerListing[] }>('/sellers/listings'),
      api<{ seat_blocks: SeatBlock[] }>('/sellers/seat-blocks'),
      api<{ games: Game[] }>('/games'),
    ]);
    setListings(a.listings);
    setSeatBlocks(b.seat_blocks);
    setGames(g.games);
  }
  useEffect(() => {
    refresh().catch(console.error);
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api('/listings', {
        method: 'POST',
        body: JSON.stringify({
          seat_block_id: form.seat_block_id,
          game_id: form.game_id,
          price_per_seat_cents: Math.round(Number(form.price) * 100),
          quantity_available: form.qty,
          listing_note: form.note,
        }),
      });
      setForm({ seat_block_id: '', game_id: '', price: '', qty: 1, note: '' });
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'error';
      setError(
        msg.includes('stripe_onboarding_incomplete')
          ? 'Finish your Stripe payouts setup before creating listings.'
          : msg.includes('seat_block_unverified')
            ? 'That seat block hasn’t been verified yet.'
            : 'Could not create the listing. Please check your details and try again.'
      );
    }
  }

  async function cancel(id: string) {
    await api(`/listings/${id}`, { method: 'DELETE' });
    await refresh();
  }

  return (
    <section>
      <h2>My listings</h2>
      <SignedOut>
        <SignInButton mode="modal" />
      </SignedOut>
      <SignedIn>
        <form className="card" onSubmit={create} style={{ marginBottom: '1.5rem' }}>
          <h3>New listing</h3>
          <label>
            <span>Seat block</span>
            <select
              required
              value={form.seat_block_id}
              onChange={(e) => setForm({ ...form, seat_block_id: e.target.value })}
            >
              <option value="">Select…</option>
              {seatBlocks
                .filter((sb) => sb.verified)
                .map((sb) => (
                  <option key={sb.seat_block_id} value={sb.seat_block_id}>
                    Sec {sb.section} Row {sb.row} — {sb.seat_count} seats
                  </option>
                ))}
            </select>
          </label>
          <label>
            <span>Game</span>
            <select
              required
              value={form.game_id}
              onChange={(e) => setForm({ ...form, game_id: e.target.value })}
            >
              <option value="">Select…</option>
              {games.map((g) => (
                <option key={g.game_id} value={g.game_id}>
                  {g.opponent} — {g.game_date}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Price per seat (USD)</span>
            <input
              required
              type="number"
              step="1"
              min="1"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
            />
          </label>
          <label>
            <span>Quantity</span>
            <input
              required
              type="number"
              min="1"
              value={form.qty}
              onChange={(e) => setForm({ ...form, qty: Number(e.target.value) })}
            />
          </label>
          <label>
            <span>Note (optional)</span>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </label>
          <button>Create listing</button>
          {error && <p style={{ color: 'crimson' }}>{error}</p>}
        </form>

        <div className="grid cols-3">
          {listings.map((l) => (
            <div key={l.listing_id} className="card">
              <p>
                <strong>{l.opponent}</strong> — {l.game_date}
              </p>
              <p className="muted">
                Sec {l.section} Row {l.row} · {l.quantity_available - l.quantity_sold} left
              </p>
              <p className="price">{formatPrice(l.price_per_seat_cents)} / seat</p>
              <p>
                <span className="badge">{l.status}</span>
              </p>
              {l.status === 'active' && (
                <button className="btn-secondary" onClick={() => cancel(l.listing_id)}>
                  Cancel
                </button>
              )}
            </div>
          ))}
        </div>
      </SignedIn>
    </section>
  );
}
